import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@core/api/useApiClient';
import { adminApi } from '@core/api/adminApi';
import { queryKeys } from '@core/api/queryKeys';
import type { AdminCapabilities } from '@core/api/adminTypes';

/** Default while loading / unreachable: assume everything is available so the UI is never gated by mistake. */
const ALL_CAPS: AdminCapabilities = {
  approvals: true, cqg: true, registrationAudit: true, actionFlags: true, positions: true,
  aiCredits: true, aiUsage: true, infra: true, subscriptions: true, creditPacks: true,
  aiStrategies: true, compliance: true, complianceCanResolveAi: false,
};

/**
 * Which schema features the connected backend DB can serve. Used to hide sections whose data the
 * backend can't provide (e.g. against PROD's older journal DB). Capabilities change only on a backend
 * restart, so we cache them effectively forever (`staleTime: Infinity`) and never refetch in-session.
 *
 * Returns a guaranteed-non-undefined `AdminCapabilities` (defaults to all-true while loading) so callers
 * can gate with a plain boolean without juggling `undefined`.
 *
 * `compliance` is the exception once the backend has answered: it is on only when the backend says
 * `true`. A backend that predates the console sends no such field, and the console must stay hidden
 * there rather than poll endpoints that do not exist (fail closed). `complianceCanResolveAi` too: it grants an act,
 * so it is true only when the backend says so for this caller, and false while loading.
 */
export function useCapabilities(): AdminCapabilities {
  const api = useApiClient();
  const { data } = useQuery<AdminCapabilities>({
    queryKey: queryKeys.capabilities(),
    queryFn:  () => adminApi.capabilities(api),
    staleTime: Infinity,
    gcTime: Infinity,
  });
  return data ? { ...data, compliance: data.compliance === true, complianceCanResolveAi: data.complianceCanResolveAi === true } : ALL_CAPS;
}
